import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const emptyMember = {
  id: "",
  member_image: [],
  member_name: "",
  member_post: "",
  member_description: "",
};

const TeamMemberModal = ({
  showModal,
  selectedMember,
  previewImage,
  handleChange,
  handleImageChange,
  handleSubmit,
  closeModal,
  submitting,
  modalMode,
  message,
  error,
}) => {
  if (!showModal || !selectedMember) return null;

  return createPortal(
    <div style={styles.modalOverlay} onClick={closeModal}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {modalMode === "add" ? "Add Team Member" : "Edit Team Member"}
          </h3>

          <button type="button" onClick={closeModal} style={styles.closeBtn}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.imageBox}>
            <label style={styles.label}>Member Image</label>

            {previewImage ? (
              <img
                src={previewImage}
                alt="Team Member Preview"
                style={styles.image}
              />
            ) : (
              <div style={styles.noImageBox}>No image selected</div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Member Name</label>
            <input
              type="text"
              name="member_name"
              value={selectedMember.member_name || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter member name"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Member Post</label>
            <input
              type="text"
              name="member_post"
              value={selectedMember.member_post || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter member post"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Member Description</label>
            <textarea
              name="member_description"
              value={selectedMember.member_description || ""}
              onChange={handleChange}
              rows="5"
              style={styles.textarea}
              placeholder="Enter member description"
              required
            />
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={submitting}>
            {submitting
              ? "Saving..."
              : modalMode === "add"
              ? "Add Team Member"
              : "Update Team Member"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

const TeamMembersEditor = () => {
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit");

  const [teamHeading, setTeamHeading] = useState({
    title: "",
    text: "",
  });
  const [headingLoading, setHeadingLoading] = useState(false);
  const [headingSubmitting, setHeadingSubmitting] = useState(false);
  const [headingMessage, setHeadingMessage] = useState("");
  const [headingError, setHeadingError] = useState("");

  useEffect(() => {
    fetchMembers();
    fetchTeamHeading();
  }, []);

  const fetchTeamHeading = async () => {
    try {
      setHeadingLoading(true);
      setHeadingError("");

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/team-member-headingtitle"
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Failed to fetch heading data");
      }

      setTeamHeading({
        title: result?.data?.title || "",
        text: result?.data?.text || "",
      });
    } catch (err) {
      setHeadingError(err.message || "Something went wrong while fetching heading");
    } finally {
      setHeadingLoading(false);
    }
  };

  const handleHeadingChange = (e) => {
    const { name, value } = e.target;

    setTeamHeading((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleHeadingSubmit = async (e) => {
    e.preventDefault();
    setHeadingSubmitting(true);
    setHeadingMessage("");
    setHeadingError("");

    try {
      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/team-member-heading-title",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: teamHeading.title,
            text: teamHeading.text,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Failed to save heading content");
      }

      setHeadingMessage(result.message || "Heading content saved successfully");
      await fetchTeamHeading();
    } catch (err) {
      setHeadingError(err.message || "Something went wrong while saving heading");
    } finally {
      setHeadingSubmitting(false);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/team-member-list"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status && result.data) {
        const memberData = Array.isArray(result.data)
          ? result.data
          : [result.data];

        setMembers(memberData);
      } else {
        throw new Error(result.message || "Failed to fetch team members");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setSelectedMember({ ...emptyMember });
    setPreviewImage("");
    setMessage("");
    setError("");
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setModalMode("edit");
    setSelectedMember({
      ...member,
      member_image: member.member_image ? [member.member_image] : [],
    });

    setPreviewImage(
      Array.isArray(member.member_image)
        ? member.member_image[0] || ""
        : member.member_image || ""
    );

    setMessage("");
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMember(null);
    setPreviewImage("");
    setMessage("");
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSelectedMember((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);

      setSelectedMember((prev) => ({
        ...prev,
        member_image: [file],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const payload = new FormData();

      payload.append("member_name", selectedMember.member_name || "");
      payload.append("member_post", selectedMember.member_post || "");
      payload.append(
        "member_description",
        selectedMember.member_description || ""
      );

      if (
        selectedMember.member_image &&
        selectedMember.member_image.length > 0 &&
        selectedMember.member_image[0] instanceof File
      ) {
        payload.append("member_image", selectedMember.member_image[0]);
      }

      let apiUrl = "https://eventscons.com/eventbackendnew/api/team-member-add";

      if (modalMode === "edit") {
        apiUrl = "https://eventscons.com/eventbackendnew/api/team-member-update";
        payload.append("id", selectedMember.id);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(
          result.message ||
            (modalMode === "add"
              ? "Team member add failed"
              : "Team member update failed")
        );
      }

      setMessage(
        result.message ||
          (modalMode === "add"
            ? "Team member added successfully"
            : "Team member updated successfully")
      );

      await fetchMembers();

      setTimeout(() => {
        closeModal();
      }, 800);
    } catch (err) {
      setError(err.message || "Something went wrong while saving");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this team member?"
    );

    if (!confirmDelete) return;

    try {
      setDeletingId(id);
      setMessage("");
      setError("");

      const response = await fetch(
        `https://eventscons.com/eventbackendnew/api/team-member-delete/${id}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Delete failed");
      }

      setMessage(result.message || "Team member deleted successfully");
      await fetchMembers();
    } catch (err) {
      setError(err.message || "Something went wrong while deleting");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={styles.loading}>Loading team members...</p>;
  if (error && members.length === 0) return <p style={styles.error}>{error}</p>;

  return (
    <>
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.headerRow}>
            <div>
              <h2 style={styles.heading}>Team Members</h2>
            </div>

            <button
              type="button"
              style={styles.addButton}
              onClick={openAddModal}
            >
              + Add Team Member
            </button>
          </div>

          <div style={styles.innerSection}>
            {/* <h3 style={styles.innerSectionTitle}>Team Section Heading</h3> */}

            <form onSubmit={handleHeadingSubmit} style={styles.innerForm}>
              <div style={styles.field}>
                <label style={styles.label}>Main Heading</label>
                <input
                  type="text"
                  name="title"
                  value={teamHeading.title}
                  onChange={handleHeadingChange}
                  style={styles.input}
                  placeholder="Enter heading"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Main Text</label>
                <textarea
                  name="text"
                  value={teamHeading.text}
                  onChange={handleHeadingChange}
                  rows="4"
                  style={styles.textarea}
                  placeholder="Enter text"
                  required
                />
              </div>

              {headingLoading && (
                <p style={styles.infoText}>Loading heading data...</p>
              )}
              {headingMessage && <p style={styles.success}>{headingMessage}</p>}
              {headingError && <p style={styles.error}>{headingError}</p>}

              <button
                type="submit"
                style={styles.button}
                disabled={headingSubmitting}
              >
                {headingSubmitting ? "Saving..." : "Save Heading Content"}
              </button>
            </form>
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          {members.length === 0 ? (
            <div style={styles.emptyBox}>
              <p style={styles.emptyText}>No team members found.</p>
              <button
                type="button"
                style={styles.addButton}
                onClick={openAddModal}
              >
                + Add First Member
              </button>
            </div>
          ) : (
            <div style={styles.grid}>
              {members.map((member) => (
                <div key={member.id} style={styles.card}>
                  <div style={styles.cardImageWrap}>
                    <img
                      src={
                        Array.isArray(member.member_image)
                          ? member.member_image[0] || ""
                          : member.member_image || ""
                      }
                      alt={member.member_name || "Team Member"}
                      style={styles.cardImage}
                    />

                    <button
                      style={styles.editBtn}
                      onClick={() => openEditModal(member)}
                      type="button"
                      title="Edit Team Member"
                    >
                      ✏️
                    </button>
                  </div>

                  <div style={styles.cardBody}>
                    <h3 style={styles.cardTitle}>{member.member_name}</h3>
                    <p style={styles.cardPost}>{member.member_post}</p>
                    <p style={styles.cardText}>{member.member_description}</p>

                    <button
                      type="button"
                      style={{
                        ...styles.deleteBtn,
                        opacity: deletingId === member.id ? 0.7 : 1,
                      }}
                      onClick={() => handleDelete(member.id)}
                      disabled={deletingId === member.id}
                    >
                      {deletingId === member.id
                        ? "Deleting..."
                        : "Delete Member"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <TeamMemberModal
        showModal={showModal}
        selectedMember={selectedMember}
        previewImage={previewImage}
        handleChange={handleChange}
        handleImageChange={handleImageChange}
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        submitting={submitting}
        message={message}
        error={error}
        modalMode={modalMode}
      />
    </>
  );
};

const styles = {
  section: {
    padding: "40px 20px",
    border: "1px solid #eee",
    background: "#fff",
    margin: "30px",
    boxShadow: "0px 10px 25px #dcdcdc",
    borderRadius: "15px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "25px",
    flexWrap: "wrap",
  },
  heading: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    textAlign: "left",
  },
  subHeading: {
    margin: "8px 0 0 0",
    color: "#6b7280",
    fontSize: "15px",
    fontWeight: "500",
  },
  innerSection: {
    marginBottom: "30px",
    padding: "20px",
    border: "1px solid #ececec",
    borderRadius: "12px",
    background: "#fafafa",
  },
  innerSectionTitle: {
    margin: "0 0 16px 0",
    fontSize: "20px",
    fontWeight: "700",
    color: "#222",
  },
  innerForm: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  infoText: {
    color: "#6b7280",
    fontWeight: "500",
    marginBottom: "8px",
  },
  addButton: {
    padding: "12px 18px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    border: "1px solid #efefef",
  },
  cardImageWrap: {
    position: "relative",
  },
  cardImage: {
    width: "100%",
    height: "220px",
    objectFit: "contain",
    padding: "10px",
    display: "block",
    background: "#f5f5f5",
  },
  cardBody: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#222",
  },
  cardPost: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "700",
    color: "#9B1321",
  },
  cardText: {
    margin: 0,
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#5b5b5b",
    minHeight: "72px",
  },
  editBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    background: "#9B1321",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },
  deleteBtn: {
    padding: "12px 16px",
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  emptyBox: {
    padding: "40px 20px",
    textAlign: "center",
    border: "1px dashed #d1d5db",
    borderRadius: "14px",
    background: "#fafafa",
  },
  emptyText: {
    margin: "0 0 16px 0",
    color: "#6b7280",
    fontWeight: "500",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 99999,
  },
  modal: {
    position: "relative",
    width: "100%",
    maxWidth: "820px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#222",
    margin: 0,
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "30px",
    cursor: "pointer",
    color: "#444",
    lineHeight: 1,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  image: {
    width: "100%",
    maxWidth: "100%",
    height: "260px",
    objectFit: "cover",
    borderRadius: "10px",
    border: "1px solid #ddd",
  },
  noImageBox: {
    height: "180px",
    border: "1px dashed #bbb",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    background: "#fafafa",
    fontWeight: "500",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    resize: "vertical",
    width: "100%",
    boxSizing: "border-box",
  },
  button: {
    padding: "14px 20px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  success: {
    color: "green",
    fontWeight: "600",
    marginBottom: "16px",
  },
  error: {
    color: "red",
    fontWeight: "600",
    marginBottom: "16px",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
  },
};

export default TeamMembersEditor;